import { FEATURE_ROUTES } from "../../feature-routes";
import { stripRoutePrefix } from "./app-route-prefix.service";

/**
 * Access requirements declared on a route in FEATURE_ROUTES. These are the same
 * `data.permissions`/`data.roles` the RoleGuard reads to gate navigation and the
 * API uses to authorize, so deriving from them keeps menu visibility and access
 * rules from drifting apart (ADO 1490 AC3).
 */
export interface RouteAccessRequirements {
  readonly permissions: string[];
  readonly roles: string[];
}

const EMPTY_ACCESS: RouteAccessRequirements = { permissions: [], roles: [] };

/**
 * Derive the declared access requirements for a nav item's route by looking up the
 * matching FEATURE_ROUTES entry. Returns the route's declared `permissions`/`roles`
 * (empty arrays when the route is unknown or declares none). The lookup strips the
 * app route prefix consistently with how {@link addRoutePrefix} adds it, so both a
 * bare "/phms/reports" and a prefixed "/app/phms/reports" resolve to the same route.
 */
export function deriveRouteAccess(
  route: string | null | undefined,
  routePrefix?: string | null,
): RouteAccessRequirements {
  const normalizedPath = normalizeFeatureRoutePath(route, routePrefix);
  if (!normalizedPath) {
    return EMPTY_ACCESS;
  }

  const match = FEATURE_ROUTES.find(
    (candidate) => typeof candidate.path === "string" && normalizePath(candidate.path) === normalizedPath,
  );
  if (!match) {
    return EMPTY_ACCESS;
  }

  const data = match.data ?? {};
  return {
    permissions: toStringArray(data["permissions"]),
    roles: toStringArray(data["roles"]),
  };
}

/**
 * Normalize a nav route to the FEATURE_ROUTES path shape: strip the app route
 * prefix and any leading/trailing slashes so "/app/phms/reports" and
 * "/phms/reports" both become "phms/reports".
 */
function normalizeFeatureRoutePath(
  route: string | null | undefined,
  routePrefix?: string | null,
): string {
  const stripped = stripRoutePrefix(route, routePrefix);
  if (!stripped) {
    return "";
  }

  return normalizePath(stripped);
}

function normalizePath(path: string): string {
  return path.replace(/^\/+/, "").replace(/\/+$/, "");
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0);
}
